/**
 * cxGuardClient — the stock-in form's line to the Stock-In Safety Engine (/api/cx).
 *
 * Deliberately NOT services/httpClient.js:
 *   • httpClient.post() broadcasts signalDataChanged on every success, which would make
 *     other screens refetch on every check;
 *   • it redirects to /login on a 401 — a guard that is only advisory must never do that;
 *   • it ignores timeoutMs — the guard has a hard 3 s budget (design §5.1 step 1).
 * Same base URL and the same auth headers, read without side effects. EVERY failure —
 * no fetch, timeout, network, non-2xx, malformed body — rejects, and the form then behaves
 * exactly as it did before the guard existed (fail-open).
 */

const normalizeBase = (raw) => {
  const s = String(raw || '').trim().replace(/\/+$/, '');
  if (!s) return '';
  return s.endsWith('/api') ? s : `${s}/api`;
};
const API_BASE = normalizeBase(process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL) || '/api';
const QUEUE_KEY = 'cxDecisionQueue';
const QUEUE_MAX = 50;

const safeGet = (key) => {
  try { return window.localStorage.getItem(key); } catch (_) { return null; }
};
const safeSet = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch (_) { /* private mode */ }
};

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const token = safeGet('token') || safeGet('authToken');
  if (token) h.Authorization = `Bearer ${token}`;
  const businessId = safeGet('businessId');
  if (businessId) h['X-Business-Id'] = businessId;
  return h;
}

async function call(method, path, body, timeoutMs) {
  if (typeof fetch !== 'function') throw new Error('cx: fetch unavailable');
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: headers(),
      body: body ? JSON.stringify(body) : undefined,
      signal: ctrl ? ctrl.signal : undefined,
    });
    if (!res || !res.ok) throw new Error(`cx: HTTP ${res ? res.status : 'none'}`);
    const json = await res.json();
    if (!json || json.success !== true || json.data === undefined) throw new Error('cx: bad response');
    return json.data;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export const cxGetSettings = () => call('GET', '/cx/settings', null, 3000);
export const cxItemContext = (payload) => call('POST', '/cx/item-context', payload, 4000);
export const cxCheckLines = (payload) => call('POST', '/cx/check-lines', payload, 3000);
// Layer 4 — Settings → Price history (read only; { enabled: false } while its switch is off)
export const cxPriceHistory = () => call('GET', '/cx/price-history', null, 15000);
export const cxPriceHistoryItem = (itemId) => call('GET', `/cx/price-history/${encodeURIComponent(itemId)}`, null, 15000);

function readQueue() {
  try { return JSON.parse(safeGet(QUEUE_KEY) || '[]') || []; } catch (_) { return []; }
}
function writeQueue(q) {
  safeSet(QUEUE_KEY, JSON.stringify(q.slice(-QUEUE_MAX)));
}

/**
 * Fire-and-forget (design §5.1 step 9): 3 tries 1 s / 2 s / 4 s apart, then queued in this
 * browser and retried on the next form mount. Never throws, never blocks the person.
 */
export function cxRecordDecisions(payload) {
  const attempt = (n) => {
    call('POST', '/cx/record-decisions', payload, 8000).catch(() => {
      if (n < 3) {
        setTimeout(() => attempt(n + 1), 1000 * 2 ** n);
      } else {
        writeQueue([...readQueue(), { payload, at: Date.now() }]);
      }
    });
  };
  attempt(0);
}

export async function cxFlushDecisionQueue() {
  const q = readQueue();
  if (!q.length) return 0;
  const left = [];
  for (const entry of q) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await call('POST', '/cx/record-decisions', entry.payload, 8000);
    } catch (_) {
      if (Date.now() - (entry.at || 0) < 7 * 24 * 3600 * 1000) left.push(entry);
    }
  }
  writeQueue(left);
  return q.length - left.length;
}
